import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FaqList, { type FaqItem } from "@/components/feature/FaqList";
import { PILLAR_ARTICLES } from "@/mocks/articlesMeta";
import { ARTICLES_EN } from "@/mocks/articlesEn";
import { ARTICLES_FR } from "@/mocks/articlesFr";
import { ARTICLES_DA } from "@/mocks/articlesDa";
import { ARTICLES_DE } from "@/mocks/articlesDe";
import { MAIN_SITE_URL } from "@/mocks/categories";

interface ArticleSection {
  h: string;
  p: string[];
}
interface ArticleFaq {
  q: string;
  a: string;
}
interface ArticleLocalized {
  title: string;
  excerpt: string;
  lead: string;
  sections: ArticleSection[];
  faq: ArticleFaq[];
}

const CONTENT_MAPS: Record<string, Record<string, ArticleLocalized>> = {
  en: ARTICLES_EN as unknown as Record<string, ArticleLocalized>,
  fr: ARTICLES_FR as unknown as Record<string, ArticleLocalized>,
  da: ARTICLES_DA as unknown as Record<string, ArticleLocalized>,
  de: ARTICLES_DE as unknown as Record<string, ArticleLocalized>,
};

function pickLang(language: string): string {
  if (language?.startsWith("fr")) return "fr";
  if (language?.startsWith("da")) return "da";
  if (language?.startsWith("de")) return "de";
  return "en";
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const metaIndex = PILLAR_ARTICLES.findIndex((a) => a.slug === slug);
  if (metaIndex === -1) {
    return <Navigate to="/" replace />;
  }

  const meta = PILLAR_ARTICLES[metaIndex];
  const lang = pickLang(i18n.language);
  const content = CONTENT_MAPS[lang]?.[slug ?? ""] ?? CONTENT_MAPS.en[slug ?? ""];

  const faqItems: FaqItem[] = content.faq.map((f) => ({ q: f.q, a: f.a }));

  // Rotate a small set of related guides based on position
  const related = [1, 2, 3].map((offset) => {
    const idx = (metaIndex + offset) % PILLAR_ARTICLES.length;
    return PILLAR_ARTICLES[idx];
  });

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <img
            src={meta.image}
            alt={content.title}
            title={content.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
          <div className="relative z-10 px-4 md:px-6 py-16 md:py-24 max-w-3xl mx-auto text-center">
            <nav className="text-xs md:text-sm text-background-50/70 mb-4">
              <Link to="/" className="hover:text-background-50 transition-colors">
                {t("nav.home")}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-background-50/90">{content.title}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight tracking-tight">
              {content.title}
            </h1>
            <p className="mt-3 text-xs md:text-sm text-background-50/70">
              {t("article.updated")}
            </p>
          </div>
        </section>

        {/* Body */}
        <article className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <p className="text-base md:text-lg text-foreground-800 leading-relaxed">
              {content.lead}
            </p>

            {content.sections.map((s, i) => (
              <section key={i} className="mt-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  {s.h}
                </h2>
                {s.p.map((paragraph, j) => (
                  <p
                    key={j}
                    className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            {/* Internal link back to categories */}
            <div className="mt-10 p-5 rounded-lg bg-accent-100/60 border border-accent-200">
              <p className="text-sm text-foreground-700">
                <i className="ri-links-line mr-1.5 text-accent-700" />
                <Link
                  to="/#categories"
                  className="font-semibold text-accent-800 underline hover:text-accent-900"
                >
                  {t("categories.title")}
                </Link>{" "}
                — {t("article.cta.text")}
              </p>
            </div>

            {/* FAQ */}
            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {t("faq.title")}
              </h2>
              <FaqList items={faqItems} />
            </section>

            {/* Related guides */}
            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {t("articles.related")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((r) => {
                  const rLang = CONTENT_MAPS[lang]?.[r.slug] ?? CONTENT_MAPS.en[r.slug];
                  return (
                    <Link
                      key={r.slug}
                      to={`/article/${r.slug}`}
                      className="group p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-3">
                        <i className={`${r.icon} text-lg`} />
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900 leading-snug">
                        {rLang.title}
                      </h3>
                    </Link>
                  );
                })}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-12 p-6 md:p-8 rounded-lg bg-primary-500 text-center">
              <h2 className="text-xl md:text-2xl font-bold text-background-50">
                {t("article.cta.title")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-50/90">
                {t("article.cta.text")}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-foreground-900 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
                >
                  {t("article.cta.button")}
                  <i className="ri-arrow-right-up-line text-base" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors"
                >
                  {t("article.back")}
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}